import {
  DialogContent,
  Dialog as MuiDialog,
  DialogTitle,
  IconButton,
  Stack,
} from '@mui/material';
import Details from './Details';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { Contributor, ProductMeta } from '../../types/DocumentTypes';
import { useMemo } from 'react';

export default function Dialog({
  open,
  onClose,
  productMeta,
}: Readonly<{
  open: boolean;
  onClose: () => void;
  productMeta: ProductMeta | null;
}>) {
  const { t } = useTranslation();

  const authors = useMemo(() => {
    if (!productMeta || !Array.isArray(productMeta.contributors)) {
      return [];
    }

    return productMeta.contributors
      .filter((c: Contributor) => c.role === 'Author')
      .map((c: Contributor) => c.name);
  }, [productMeta]);

  if (!productMeta) {
    return null;
  }

  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            borderRadius: '4px',
            maxWidth: '480px',
            width: '100%',
            margin: { xs: 1.5, sm: 3 },
          },
        },
      }}
    >
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <DialogTitle
          component='h3'
          variant='h4'
          sx={{ paddingX: 2, flexGrow: 1, paddingY: 0 }}
        >
          {t('readingView.aboutBook.title')}
        </DialogTitle>
        <IconButton
          aria-label={t('readingView.aboutBook.close')}
          onClick={onClose}
          size='large'
        >
          <CloseIcon role='presentation' />
        </IconButton>
      </Stack>
      <DialogContent dividers sx={{ padding: 0 }}>
        <Details
          isbn={productMeta.isbn}
          title={productMeta.title}
          authors={authors}
          publicationYear={productMeta.publicationYear}
          summary={productMeta.descriptionHtml}
          coverImageUrl={productMeta.coverImageUrl}
        />
      </DialogContent>
    </MuiDialog>
  );
}
